import {
  getBrowserRefreshToken,
  obtainFreshAccessViaRefresh,
  resetRefreshTokenCaches,
} from './authRefresh';
import { isUserInitiatedLogoutGateActive } from '../auth/logoutGate';
import { decodeJwtPayload } from '../auth/jwtPayload';
import { notifySessionExpired } from '../auth/sessionExpired';
import {
  getSessionToken,
  getStoredLoginType,
  persistAccessTokenOnly,
} from '../auth/sessionToken';
import { publicPath } from '../publicPath';

/** In-memory access token from last successful `/auth/refresh` (this tab only). */
let refreshedAccessTokenMemory: string | null = null;

/** Same idea as test scripts’ `accessToken.js` skew — expired session JWT must not win over fresh `token.txt`. */
export const API_TOKEN_EXP_SKEW_SEC = 60;

function isBearerStillValidForApi(token: string, skewSec: number): boolean {
  const t = token.trim();
  if (!t) {
    return false;
  }
  const payload = decodeJwtPayload(t);
  if (!payload) {
    return true;
  }
  const exp = payload.exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) {
    return true;
  }
  const now = Math.floor(Date.now() / 1000);
  return exp > now + skewSec;
}

/** Whether a Bearer token is still usable for API calls (default skew). */
export function isAccessTokenValidForApi(token: string): boolean {
  return isBearerStillValidForApi(token, API_TOKEN_EXP_SKEW_SEC);
}

/**
 * Always re-fetches `public/token.txt` (Node `tokenStorage.getToken()` reads disk every time; we must not keep
 * the first response forever or edits / script refresh never show up in the browser).
 */
async function readPublicTokenTxt(): Promise<string | null> {
  try {
    const res = await fetch(publicPath('/token.txt'), { cache: 'no-store' });
    if (!res.ok) {
      return null;
    }
    const raw = (await res.text()).trim().replace(/^\uFEFF/, '');
    if (!raw) {
      return null;
    }
    const line =
      raw
        .split(/\r?\n/)
        .map((l) => l.trim())
        .find((l) => l.length > 0 && !l.startsWith('#')) ?? null;
    return line || null;
  } catch {
    return null;
  }
}

/** Where the Bearer token came from (for UI/debug). */
export type ApiTokenSource = 'session' | 'env' | 'token.txt' | 'refreshed' | 'none';

/** How long before access JWT `exp` (after {@link API_TOKEN_EXP_SKEW_SEC}) we proactively POST `/auth/refresh`. `0` disables. */
function refreshBeforeExpirySeconds(): number {
  const raw = process.env.REACT_APP_REFRESH_BEFORE_EXPIRY_SEC?.trim();
  if (raw === '0') {
    return 0;
  }
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) {
    return Math.floor(n);
  }
  return 300;
}

/**
 * If the chosen access token expires within {@link refreshBeforeExpirySeconds}, obtain a new one via refresh
 * (when a refresh credential exists). Keeps sessions alive without manual re-login.
 */
async function upgradeTokenIfNearExpiry(
  token: string | null,
  source: ApiTokenSource,
): Promise<{ token: string | null; source: ApiTokenSource }> {
  if (!token || source === 'none') {
    return { token, source };
  }
  if (process.env.REACT_APP_AUTH_REFRESH_DISABLED === 'true') {
    return { token, source };
  }
  const windowSec = refreshBeforeExpirySeconds();
  if (windowSec <= 0) {
    return { token, source };
  }
  const skew = API_TOKEN_EXP_SKEW_SEC;
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) {
    return { token, source };
  }
  const now = Math.floor(Date.now() / 1000);
  const secondsLeftAfterSkew = exp - now - skew;
  if (secondsLeftAfterSkew > windowSec) {
    return { token, source };
  }
  const rt = await getBrowserRefreshToken();
  if (!rt?.trim()) {
    return { token, source };
  }
  const fresh = await obtainFreshAccessViaRefresh();
  if (fresh && isBearerStillValidForApi(fresh, skew)) {
    refreshedAccessTokenMemory = fresh;
    persistAccessTokenOnly(fresh);
    return { token: fresh, source: 'refreshed' };
  }
  return { token, source };
}

async function computeResolvedApiTokenWithMeta(): Promise<{ token: string | null; source: ApiTokenSource }> {
  const skew = API_TOKEN_EXP_SKEW_SEC;

  if (refreshedAccessTokenMemory && isBearerStillValidForApi(refreshedAccessTokenMemory, skew)) {
    return { token: refreshedAccessTokenMemory, source: 'refreshed' };
  }

  const fromEnv = process.env.REACT_APP_API_TOKEN?.trim();
  const fromFile = await readPublicTokenTxt();
  const session = getSessionToken();
  const sessionFresh = Boolean(session && isBearerStillValidForApi(session, skew));
  const uiLoginSession = sessionFresh && Boolean(getStoredLoginType());

  if (uiLoginSession && session) {
    return { token: session, source: 'session' };
  }
  if (fromEnv && isBearerStillValidForApi(fromEnv, skew)) {
    return { token: fromEnv, source: 'env' };
  }
  if (fromFile && isBearerStillValidForApi(fromFile, skew)) {
    return { token: fromFile, source: 'token.txt' };
  }
  if (sessionFresh && session) {
    return { token: session, source: 'session' };
  }

  const newAccess =
    isUserInitiatedLogoutGateActive() && !getStoredLoginType()
      ? null
      : await obtainFreshAccessViaRefresh();
  if (newAccess && isBearerStillValidForApi(newAccess, skew)) {
    refreshedAccessTokenMemory = newAccess;
    persistAccessTokenOnly(newAccess);
    return { token: newAccess, source: 'refreshed' };
  }

  if (session && getStoredLoginType() && !isBearerStillValidForApi(session, skew)) {
    notifySessionExpired();
    return { token: null, source: 'none' };
  }

  if (session) {
    return { token: session, source: 'session' };
  }
  if (fromEnv) {
    return { token: fromEnv, source: 'env' };
  }
  if (fromFile) {
    return { token: fromFile, source: 'token.txt' };
  }
  return { token: null, source: 'none' };
}

/**
 * Mirrors `getValidAccessToken()` + your Node booking script (file + refresh), adapted for the browser.
 *
 * **Fresh** token order: in-memory refresh → **login session** (when signed in via UI) → **env** → **`public/token.txt`** → **session**.
 * `token.txt` still wins when there is no UI login type in storage (optional `public/token.txt` dev workflow).
 *
 * If nothing is fresh → **`POST /auth/refresh`** when configured → else **stale** session / env / file.
 *
 * Then, when **`REACT_APP_REFRESH_BEFORE_EXPIRY_SEC`** is not **`0`**, if the chosen JWT still validates but expires
 * within that window (default 300s), **`/auth/refresh`** runs automatically so access stays fresh without manual steps.
 */
export async function resolveApiTokenWithMeta(): Promise<{ token: string | null; source: ApiTokenSource }> {
  const base = await computeResolvedApiTokenWithMeta();
  return upgradeTokenIfNearExpiry(base.token, base.source);
}

/**
 * Resolves the Bearer token for API calls.
 *
 * 1. Fresh **refreshed** memory, **env**, **`public/token.txt`** (always re-read), **session** (JWT `exp` skew).
 * 2. Else **`POST /auth/refresh`** when refresh token is configured.
 * 3. Else **stale** session / env / file.
 */
export async function resolveApiToken(): Promise<string | null> {
  const { token } = await resolveApiTokenWithMeta();
  return token;
}

/** What the app will send on API calls + whether the JWT `exp` is still OK (for the Test connection panel). */
export type ApiTokenHealth = {
  hasToken: boolean;
  source: ApiTokenSource;
  sourceDescription: string;
  bearerCharCount: number;
  expUnix: number | null;
  iatUnix: number | null;
  /** Seconds until `exp` (negative = already expired). */
  secondsUntilExp: number | null;
  /** True if this resolved token is used for `Authorization` (skew applied). */
  validForApi: boolean;
  /** `localStorage` has a session JWT that is expired, so it was skipped in favor of env / `token.txt`. */
  staleSessionInStorage: boolean;
  /** `REACT_APP_REFRESH_TOKEN` or `public/refreshtoken.txt` yields a value. */
  refreshConfigured: boolean;
  decodeNote: string;
};

function apiTokenSourceDescription(source: ApiTokenSource): string {
  switch (source) {
    case 'session':
      return 'Login session (localStorage)';
    case 'env':
      return 'REACT_APP_API_TOKEN';
    case 'token.txt':
      return 'public/token.txt';
    case 'refreshed':
      return 'POST /auth/refresh (new access token)';
    default:
      return 'None';
  }
}

export async function getApiTokenHealth(): Promise<ApiTokenHealth> {
  const rawSession = getSessionToken();
  const staleSessionInStorage = Boolean(
    rawSession && !isBearerStillValidForApi(rawSession, API_TOKEN_EXP_SKEW_SEC),
  );
  const refreshConfigured = Boolean((await getBrowserRefreshToken())?.trim());

  const { token, source } = await resolveApiTokenWithMeta();

  if (!token) {
    return {
      hasToken: false,
      source: 'none',
      sourceDescription: apiTokenSourceDescription('none'),
      bearerCharCount: 0,
      expUnix: null,
      iatUnix: null,
      secondsUntilExp: null,
      validForApi: false,
      staleSessionInStorage,
      refreshConfigured,
      decodeNote: staleSessionInStorage
        ? `Session JWT expired (not used; ${API_TOKEN_EXP_SKEW_SEC}s skew). Configure env, public/token.txt, or refresh.`
        : refreshConfigured
          ? 'No access token yet — next API call may run /auth/refresh.'
          : 'No Bearer token — sign in or add public/token.txt / REACT_APP_API_TOKEN, or set REACT_APP_REFRESH_TOKEN / public/refreshtoken.txt.',
    };
  }

  const payload = decodeJwtPayload(token);
  if (!payload) {
    const looksJwt = token.split('.').length === 3;
    return {
      hasToken: true,
      source,
      sourceDescription: apiTokenSourceDescription(source),
      bearerCharCount: token.length,
      expUnix: null,
      iatUnix: null,
      secondsUntilExp: null,
      validForApi: true,
      staleSessionInStorage,
      refreshConfigured,
      decodeNote: looksJwt
        ? 'JWT payload could not be decoded — still sent as Bearer.'
        : 'Not a three-part JWT — sent as Bearer as-is.',
    };
  }

  const expRaw = payload.exp;
  const iatRaw = payload.iat;
  const expUnix =
    typeof expRaw === 'number' && Number.isFinite(expRaw) ? expRaw : null;
  const iatUnix =
    typeof iatRaw === 'number' && Number.isFinite(iatRaw) ? iatRaw : null;
  const now = Math.floor(Date.now() / 1000);
  const secondsUntilExp = expUnix != null ? expUnix - now : null;
  const validForApi = isBearerStillValidForApi(token, API_TOKEN_EXP_SKEW_SEC);

  let decodeNote: string;
  if (expUnix == null) {
    decodeNote = 'No exp claim — treated as usable for API.';
  } else if (!validForApi) {
    decodeNote = `Expired for API (exp must be > now + ${API_TOKEN_EXP_SKEW_SEC}s).`;
  } else {
    decodeNote = `OK for API (exp clears ${API_TOKEN_EXP_SKEW_SEC}s skew).`;
  }
  if (refreshConfigured) {
    decodeNote += ' Refresh uses httpOnly cookie (credentials: include) or stored refresh token when access expires.';
  }

  return {
    hasToken: true,
    source,
    sourceDescription: apiTokenSourceDescription(source),
    bearerCharCount: token.length,
    expUnix,
    iatUnix,
    secondsUntilExp,
    validForApi,
    staleSessionInStorage,
    refreshConfigured,
    decodeNote,
  };
}

export async function buildAuthorizedHeaders(base?: Record<string, string>): Promise<Headers> {
  const headers = new Headers(base);
  const token = await resolveApiToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

/** Clears in-memory refresh result and refresh in-flight promise (e.g. logout / Jest). */
export function resetApiTokenCache(): void {
  refreshedAccessTokenMemory = null;
  resetRefreshTokenCaches();
}
